import { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const columns = [
  { Header: "Module ID", accessor: "moduleId", width: "18%", align: "left" },
  { Header: "Module", accessor: "module", width: "20%", align: "left" },
  { Header: "Sub Module", accessor: "subModule", width: "20%", align: "left" },
  { Header: "Description", accessor: "moduleDesc", width: "42%", align: "left" },
  { Header: "Action", accessor: "action", width: "0%", align: "center" },
];

function Modules() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [filterModule, setFilterModule] = useState("");
  const [filterSubModule, setFilterSubModule] = useState("");
  const [filterDesc, setFilterDesc] = useState("");
  const [moduleNames, setModuleNames] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    module: "", isNewModule: false, customModule: "", subModule: "", moduleDesc: "",
  });
  const [createErrors, setCreateErrors] = useState({});
  const api = useApi();

  const handleDeleteClick = (row) => {
    setSelectedModule(row);
    setOpenConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenConfirmDialog(false);
    setSelectedModule(null);
  };

  const deleteModule = () => {
    if (!selectedModule) return;
    const { module, subModule } = selectedModule;
    api
      .delete(`modules/${encodeURIComponent(module)}/${encodeURIComponent(subModule)}`)
      .then(() => {
        toast.success(t("modulesPage.deleteSuccess"), { id: "ModuleDeleteSuccess" });
        handleCloseDialog();
        getModules();
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error(t("no_permission_message"), { id: "ModulesDeletePermissionError" });
        } else {
          toast.error(t("modulesPage.failedToDelete"), { id: "ModuleDeleteError" });
        }
        console.error("Error deleting module:", error);
        handleCloseDialog();
      });
  };

  // ── Create module ────────────────────────────────────────────────────────
  const MODULE_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9]*$/;
  const EMPTY_CREATE_FORM = { module: "", isNewModule: false, customModule: "", subModule: "", moduleDesc: "" };

  const handleOpenCreate = () => {
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateErrors({});
    setOpenCreateDialog(true);
  };

  const handleCloseCreate = () => {
    setOpenCreateDialog(false);
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateErrors({});
  };

  const handleModuleSelect = (value) => {
    if (value === "__ADD_NEW__") {
      setCreateForm((prev) => ({ ...prev, module: "__ADD_NEW__", isNewModule: true, customModule: "" }));
    } else {
      setCreateForm((prev) => ({ ...prev, module: value, isNewModule: false, customModule: "" }));
    }
    setCreateErrors((prev) => ({ ...prev, module: undefined }));
  };

  const handleCamelCaseOnly = (field, value) => {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
    setCreateForm((prev) => ({ ...prev, [field]: cleaned }));
    setCreateErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateCreate = () => {
    const errs = {};
    const resolvedModule = createForm.isNewModule ? createForm.customModule : createForm.module;
    if (!resolvedModule) {
      errs.module = t("modulesPage.moduleRequired");
    } else if (createForm.isNewModule && !MODULE_NAME_REGEX.test(resolvedModule)) {
      errs.module = t("modulesPage.moduleNameInvalid");
    }
    if (!createForm.subModule) {
      errs.subModule = t("modulesPage.subModuleRequired");
    } else if (!MODULE_NAME_REGEX.test(createForm.subModule)) {
      errs.subModule = t("modulesPage.subModuleNameInvalid");
    }
    if (!createForm.moduleDesc.trim()) {
      errs.moduleDesc = t("modulesPage.moduleDescRequired");
    }
    return errs;
  };

  const createModule = () => {
    const errs = validateCreate();
    if (Object.keys(errs).length > 0) {
      setCreateErrors(errs);
      return;
    }
    const payload = {
      module: createForm.isNewModule ? createForm.customModule : createForm.module,
      subModule: createForm.subModule,
      moduleDesc: createForm.moduleDesc,
    };
    api
      .post("modules", payload)
      .then(() => {
        toast.success(t("modulesPage.createSuccess"), { id: "ModuleCreateSuccess" });
        handleCloseCreate();
        getModules();
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error(t("no_permission_message"), { id: "ModulesCreatePermissionError" });
        } else {
          toast.error(t("modulesPage.failedToCreate"), { id: "ModuleCreateError" });
        }
        console.error("Error creating module:", error);
      });
  };

  const buildRows = (list) =>
    list.flatMap((mod) =>
      (mod.subModules ?? []).map((sub) => {
        const row = {
          moduleId: sub.moduleId,
          module: mod.module,
          subModule: sub.subModule,
          moduleDesc: sub.moduleDesc,
        };
        row.action = (
          <MDButton
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleDeleteClick(row)}
          >
            {t("modulesPage.delete")}
          </MDButton>
        );
        return row;
      })
    ).sort((a, b) => Number(a.moduleId) - Number(b.moduleId));

  const getModules = () => {
    api
      .get("modules")
      .then((response) => {
        const list = Array.isArray(response.data?.body)
          ? response.data.body
          : Array.isArray(response.data)
            ? response.data
            : [];
        setRows(buildRows(list));
        setModuleNames(list.map((mod) => mod.module));
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error(t("no_permission_message"), { id: "ModulesPermissionError" });
          return;
        }
        toast.error(t("modulesPage.failedToLoad"), { id: "ModulesFetchError" });
        console.error("Error fetching modules:", error);
      });
  };

  useEffect(() => {
    getModules();
  }, []);

  useEffect(() => {
    let result = [...rows];
    if (filterModule) result = result.filter(r => r.module === filterModule);
    if (filterSubModule.trim()) result = result.filter(r => r.subModule?.toLowerCase().includes(filterSubModule.trim().toLowerCase()));
    if (filterDesc.trim()) result = result.filter(r => r.moduleDesc?.toLowerCase().includes(filterDesc.trim().toLowerCase()));
    setFilteredRows(result);
  }, [rows, filterModule, filterSubModule, filterDesc]);

  const resetFilters = () => {
    setFilterModule("");
    setFilterSubModule("");
    setFilterDesc("");
  };

  return (
    <DashboardLayout>
      <MDBox
        mt={3}
        mb={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          // Fills the viewport down to the screen edge: 64px navbar + 24px
          // DashboardLayout bottom padding + this box's own 24px top/bottom margins.
          height: "calc(100vh - 136px)",
        }}
      >
        <Grid container justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0, mb: 3 }}>
          <MDBox>
            <MDTypography variant="h5" fontWeight="medium">
              {t("modulesPage.title")}
            </MDTypography>
            <MDTypography variant="button" fontWeight="regular" color="secondary">
              {filteredRows.length} Records
            </MDTypography>
          </MDBox>
          <MDBox display="flex">
            <MDButton variant="gradient" color="info" onClick={handleOpenCreate}>
              {t("modulesPage.newModule")}
            </MDButton>
          </MDBox>
        </Grid>
        <MDBox
          sx={(theme) => ({
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: "8px",
            background: theme.palette.white.main,
            overflow: "hidden",
          })}
        >
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ flexShrink: 0, borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label="Module"
                    value={filterModule}
                    onChange={(e) => setFilterModule(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" }, "& .MuiSelect-select": { fontSize: "0.8rem" } }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>All</MenuItem>
                    {moduleNames.map(name => (
                      <MenuItem key={name} value={name} style={{ fontSize: "0.8rem" }}>{name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="Sub Module"
                    value={filterSubModule}
                    onChange={(e) => setFilterSubModule(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="Description"
                    value={filterDesc}
                    onChange={(e) => setFilterDesc(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg="auto">
                  <MDButton variant="outlined" color="secondary" onClick={resetFilters} size="small" style={{ fontSize: "0.75rem", padding: "5px 16px", whiteSpace: "nowrap" }}>
                    Reset
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox sx={{ flex: "1 1 auto", minHeight: 0 }}>
              <DataTable
                table={{ columns, rows: filteredRows }}
                isSorted={false}
                showTotalEntries={true}
                entriesPerPage={true}
                entriesPerPagePosition="bottom"
                bodyMaxHeight="100%"
                noEndBorder
                canSearch={false}
              />
            </MDBox>
        </MDBox>
      </MDBox>
      <Footer />

      {/* ── Create Module Dialog ─────────────────────────────────────────── */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
        <DialogTitle>{t("modulesPage.newModule")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!createErrors.module}>
                <InputLabel>{t("modulesPage.moduleLabel")}</InputLabel>
                <Select
                  value={createForm.module}
                  onChange={(e) => handleModuleSelect(e.target.value)}
                  label={t("modulesPage.moduleLabel")}
                  sx={{ height: "44px" }}
                >
                  {moduleNames.map((name) => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                  <MenuItem value="__ADD_NEW__">
                    <em>{t("modulesPage.addNew")}</em>
                  </MenuItem>
                </Select>
                {createErrors.module && !createForm.isNewModule && (
                  <FormHelperText>{createErrors.module}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {createForm.isNewModule && (
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  label={t("modulesPage.newModuleName")}
                  value={createForm.customModule}
                  onChange={(e) => handleCamelCaseOnly("customModule", e.target.value)}
                  error={!!createErrors.module}
                  helperText={createErrors.module ?? t("modulesPage.moduleNameHint")}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <MDInput
                fullWidth
                label={t("modulesPage.subModuleLabel")}
                value={createForm.subModule}
                onChange={(e) => handleCamelCaseOnly("subModule", e.target.value)}
                error={!!createErrors.subModule}
                helperText={createErrors.subModule ?? t("modulesPage.subModuleNameHint")}
              />
            </Grid>

            <Grid item xs={12}>
              <MDInput
                fullWidth
                label={t("modulesPage.moduleDescLabel")}
                value={createForm.moduleDesc}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, moduleDesc: e.target.value }))
                }
                error={!!createErrors.moduleDesc}
                helperText={createErrors.moduleDesc ?? ""}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseCreate}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="info" onClick={createModule}>
            {t("modulesPage.create")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ───────────────────────────────────── */}
      <Dialog open={openConfirmDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t("modulesPage.confirmDelete")}</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">
            {t("modulesPage.confirmDeleteMessage", {
              module: selectedModule?.module,
              subModule: selectedModule?.subModule,
            })}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseDialog}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="error" onClick={deleteModule}>
            {t("modulesPage.delete")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Modules;
